import { ReactNode } from "react";
import { useTrans } from "@/utils/useTrans";
import ImmersionBeyondComparison from "@/components/tactsuit/components/ImmersionBeyondComparison";
import { ComponentId } from "@/constants";
import { Gap } from "@/components/tactsuit/Gap";
import { FeatureGrids } from "@/v2-components/pages/tactsuit/GridsCard.tsx";
import Image from "next/image";
import { cn } from "@/libs/utils.ts";
import { LazyLoadVideo } from "@/v2-components/lazy-load-video.tsx";

export default function TactSuitAirCamoContent() {
  const { t } = useTrans(["tactsuit"]);

  return (
    <>
      {/* ⚠️ 섹션 순서는 **Figma Camo 프레임(`28716:147894`) 본문 y 좌표**를 따른다.
          Air 프레임과 마찬가지로 Purchase Benefits 다음이 **바로 Immersion** 이다.

            #3  y=4212  Immersion Across Every Platform  ← 여기가 첫 섹션
            #4  y=4780  Unseen Immersion
            #5  y=5824  Express Your Identity
            #6  y=6456  Stylish Utility: The MOLLE Interface
            #7  y=7138  Sleek Profile
            #8  y=7820  Responsive Haptic Feedback
            #9  y=8502  Agility Without Compromise
            #10 y=9184  bHaptics Companion Software      (부모가 렌더)

          예전엔 Immersion 이 **맨 끝**(Companion Software 직전)에 있었다.
          바로 위가 `ProductHeroAndBenefits`(#1 히어로 + #2 Purchase Benefits, 48/64)라
          섹션 간격은 `Gap`(64/96)이 담당한다 — Air·Air Camo 만 다른 6개 제품보다
          좁아지는 문제가 있었다. 두 컴포넌트 모두 `ProductFeatureSections` 한 곳에서만
          쓰인다.
          #5266 에서 앞에 있던 "Details" 블록(이미지 캐러셀 + 색상 스와치)도 지웠다 —
          색상은 구매 레일이, 색상별 이미지는 상단 갤러리(`?color=`)가 이미 담당한다.
          ⚠️ Camo 시안에는 `Officially Meta Certified` 가 없다 — Air 와 달리 넣지 말 것. */}
      <Gap>
        {/* 지운 Details 블록 안에 있던 `Overview` 앵커를 **첫 생존 섹션으로 옮겼다.**
            `Gap` **안**에 두는 이유는 마진 뒤 콘텐츠 시작점에 붙어야 종전 착지와
            같기 때문이다. 현재 이 앵커를 읽는 화면은 없다(읽던 `TactsuitSubMenu` 는 고아
            모듈이라 2026-10-01 삭제) — 자세한 근거는 `TactSuitAirContent` 의 같은 자리 주석 참조. */}
        <div id={ComponentId.OVERVIEW} />
        <ImmersionBeyondComparison />
      </Gap>
      <Gap className="px-4">
        <ItemLayout
          className="md:items-center"
          title={t("Unseen Immersion")}
          subTitle={t("Suit up to win, with the bold Camouflage design.")}
          contents={<UnseenImmersionContents />}
        />
      </Gap>
      <Gap className="px-4">
        <ItemLayout
          className="flex items-center"
          title={t("Express Your Identity")}
          subTitle={
            <div className="flex flex-col items-center gap-6 md:gap-5">
              <div className="text-center">
                <p>{t("From tactical patches to witty badges.")}</p>
                <p>
                  {t(
                    "The expanded Velcro zone offers increased customization options to match your mood.",
                  )}
                </p>
              </div>
              {/*//TODO Badge 배포시 주석해제 */}
              {/*<Button*/}
              {/*  onClick={() => {*/}
              {/*    router.push(`/`);*/}
              {/*  }}*/}
              {/*  size={"sm"}*/}
              {/*  shape={"pill"}*/}
              {/*  variant={"blue-fill"}*/}
              {/*  className={cn("px-6")}*/}
              {/*>*/}
              {/*  {t("View Badge Accessories")}*/}
              {/*</Button>*/}
            </div>
          }
          contents={<ExpressYourIdentityContents />}
        />
      </Gap>
      <Gap className="px-4">
        {/* 이 섹션만 4:3 이다 — `FeatureGrids` 기본값은 `aspect-square` 인데
            시안 MediaArea 가 데스크탑 `28716:154317` 586×440 · 모바일
            `28718:167817` 328×246 으로 둘 다 4:3 이다(2026-09-11 실측).
            ⚠️ 같은 페이지의 2단 섹션(`ExpressYourIdentityContents`)은 586×400 =
            1.465 로 **다른 비율**이다 — 이 값을 저기로 복사하지 말 것.
            영상도 4:3 로 다시 뽑아 파일명을 갈았다. 종전 `camo-detail-video-01`
            은 586×586 정사각이라 4:3 컨테이너에서 좌우가 잘린다. */}
        {/* CTA 는 시안 `28716:154318`(데스크탑) · `28718:167820`(모바일)에서 추가됐다.
            ⚠️ 목적지 제품이 **prod 에 아직 없다** — 2026-09-14 기준 앱이 쓰는
            `admin-apis /api/v1/shop-products` 로 재면 prod 37건 중 이 slug 0건,
            dev 35건 중 1건(개발용 등록). mgmt 의 prod 등록이 릴리스 전제다.
            같은 보드의 형제 섹션 4개 중 버튼이 있는 건 이 MOLLE 하나뿐이다
            (Sleek Profile `154323` · Responsive `154330` · Agility `154336` 전부 0건). */}
        <FeatureGrids
          reverse={true}
          imageClassName="aspect-[4/3]"
          videoSrc="https://cdn.bhaptics.com/media/tactsuit/camo-molle-interface.mp4"
          cta={{
            label: "Get Accessory",
            href: "/shop/buy-tactsuit/molle-pouch-for-tactsuit-air-camo",
          }}
          title="Stylish Utility: The MOLLE Interface"
          description="A sleek reinterpretation of tactical gear. The stylish MOLLE webbing adds both utility and a rugged aesthetic to your setup."
        />
      </Gap>
      <Gap className="px-4">
        <FeatureGrids
          imageSrc="https://cdn.bhaptics.com/images/tactsuit/tactsuit-air-camo-page/camo_sleek_profile_desktop@2x.jpg"
          title="Sleek Profile, Perfect for Seated Gameplay"
          description="Designed with a short, lightweight profile that never interferes with your gaming chair. Ideal for long sessions in the cockpit or at your desk."
        />
      </Gap>
      <Gap className="px-4">
        <FeatureGrids
          reverse={true}
          videoSrc="https://cdn.bhaptics.com/media/tactsuit/camo-detail-video-03.mp4"
          title="Responsive Haptic Feedback"
          description="16 core motors deliver instant, precise feedback. Feel every impact with zero latency."
        />
      </Gap>
      <Gap className="px-4">
        <FeatureGrids
          imageSrc="https://cdn.bhaptics.com/images/tactsuit/tactsuit-air-camo-page/camo-agility-without-compromise.jpg"
          title="Agility Without Compromise."
          description="Stay fast with breathable mesh and lag-free wireless freedom."
        />
      </Gap>
      {/* ⚠️ 여기 있던 "Reviews"(YouTube·TikTok 리뷰 영상 카드) 섹션을 제거했다.
          Figma Camo 시안(`28716:147894`)의 본문 16개 섹션에 대응 노드가 없다.

          ⚠️ **소개 페이지로 옮긴 게 아니라 사이트에서 사라진다.** 이 에픽에서
          `pages/tactsuit/tactsuit-air-camo` 가 이 PDP(`?color=camo`)로 가는 307
          리다이렉트가 됐고, 이 컴포넌트의 소비자는 PDP 하나뿐이다. 되살리려면 이
          커밋을 revert 하면 된다 — 데이터 훅(`useCamoReviewVideos`, YouTube API +
          TikTok oEmbed)도 같은 커밋에서 함께 삭제했다.

          ⚠️ `ImmersionBeyondComparison` 도 여기 있었다 — 설계상 #3(Purchase Benefits
          직후)이라 **맨 위로 옮겼다.** 여기가 마지막 섹션이고, 뒤는 부모가 렌더하는
          `bHaptics Companion Software`(#10)로 이어진다. */}
    </>
  );
}

const ItemLayout = ({
  title,
  subTitle,
  contents,
  className,
}: {
  title: ReactNode;
  subTitle?: ReactNode;
  contents: ReactNode;
  className?: string;
}) => {
  return (
    <section className="mx-auto grid w-full max-w-screen-checkout-max">
      <div
        className={cn(
          "i flex flex-col gap-3 text-coolgray-90 md:gap-5",
          className,
        )}
      >
        <h2 className="text-3xl md:text-5xl">{title}</h2>
        {subTitle && <div className="text-base md:text-lg">{subTitle}</div>}
      </div>
      <div className="mt-6 md:mt-10">{contents}</div>
    </section>
  );
};

const UnseenImmersionContents = () => (
  <section className="mx-auto">
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-[26px]">
      <div className="md:col-span-2">
        <MediaBox
          type="image"
          src="https://cdn.bhaptics.com/images/tactsuit/tactsuit-air-camo-page/camo-unseen-immersion-01.jpg"
          mobileSrc="https://cdn.bhaptics.com/images/tactsuit/tactsuit-air-camo-page/mobile-camo-unseen-immersion-01.jpg"
          className="md:h-[400px]"
        />
      </div>
      <MediaBox
        type="image"
        src="https://cdn.bhaptics.com/images/tactsuit/tactsuit-air-camo-page/camo-unseen-immersion-02.jpg"
        mobileSrc="https://cdn.bhaptics.com/images/tactsuit/tactsuit-air-camo-page/mobile-camo-unseen-immersion-02.jpg"
        className="md:h-[400px]"
      />
      <MediaBox
        type="image"
        src="https://cdn.bhaptics.com/images/tactsuit/tactsuit-air-camo-page/camo-unseen-immersion-03.jpg"
        mobileSrc="https://cdn.bhaptics.com/images/tactsuit/tactsuit-air-camo-page/mobile-camo-unseen-immersion-03.jpg"
        className="md:h-[400px]"
      />
    </div>
  </section>
);

const ExpressYourIdentityContents = () => (
  <section className="mx-auto">
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-[26px]">
      <MediaBox
        type="video"
        src="https://cdn.bhaptics.com/media/tactsuit/camo-detail-video-04.mp4"
      />
      <MediaBox
        type="video"
        src="https://cdn.bhaptics.com/media/tactsuit/camo-detail-video-02.mp4"
      />
    </div>
  </section>
);

type MediaBoxProps = {
  type: "image" | "video";
  src: string;
  mobileSrc?: string;
  alt?: string;
  className?: string;
  priority?: boolean;
};

function MediaBox({
  type,
  src,
  mobileSrc,
  alt = "",
  className = "",
  priority = false,
}: MediaBoxProps) {
  return (
    <div
      className={cn(
        "relative w-full overflow-hidden rounded-xl bg-coolgray-20",
        // 모바일 328×224 · 데스크탑 586×400 — **둘 다 41/28(1.4643)** 이라 한 값으로 덮는다.
        // 종전엔 `md:aspect-[16/10]`(1.6)이 붙어 있었는데 그 비율은 시안에 없다.
        // `md:h-[400px]` 을 넘기는 호출처(Unseen 3개)는 높이가 이기므로 가려져 있었고,
        // 안 넘기는 2개(Express Your Identity 영상)만 데스크탑에서 585×366 으로 떴다 —
        // 에셋(video-02·04)이 586×400 이라 `object-cover` 가 위아래를 잘라먹었다.
        // 실측 2026-09-11 @1440: 585×366(1.6) → 585×400(1.462). 시안 `28716:154313`.
        "aspect-[41/28]",
        className,
      )}
    >
      {type === "image" ? (
        <>
          {mobileSrc ? (
            <>
              <Image
                src={mobileSrc}
                alt={alt}
                fill
                priority={priority}
                sizes="(max-width: 768px) 100vw, 50vw"
                className="object-cover md:hidden"
              />
              <Image
                src={src}
                alt={alt}
                fill
                priority={priority}
                sizes="(max-width: 768px) 100vw, 50vw"
                className="hidden object-cover md:block"
              />
            </>
          ) : (
            <Image
              src={src}
              alt={alt}
              fill
              priority={priority}
              sizes="(max-width: 768px) 100vw, 50vw"
              className="object-cover"
            />
          )}
        </>
      ) : (
        <LazyLoadVideo
          src={src}
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
        />
      )}
    </div>
  );
}
